import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { useQuery } from '@tanstack/react-query'
import { Building2, Check, ChevronDown, ShieldCheck } from 'lucide-react'
import { useLocation, useNavigate } from 'react-router-dom'

import apiClient from '@/lib/api-client'
import { useOrgStore } from '@/lib/store'
import { MpStatusPill } from '@/ui/components'
import { cn } from '@/lib/utils'
import { buildWorkspaceSwitchPath } from '../workspace-routes'

type WorkspaceEntitlement = {
  plan?: string | null
  plan_name?: string | null
  subscription_status?: string | null
  is_active: boolean
  status_label: string
  dashboard_access_message?: string
}

type WorkspaceOption = {
  id: string
  name: string
  role: 'OWNER' | 'ADMIN' | 'VIEWER'
  role_display: string
  entitlement: WorkspaceEntitlement
}

type WorkspaceContext = {
  organization_id: string | null
  active_organization: WorkspaceOption | null
  organizations: WorkspaceOption[]
}

function workspaceLabel(name?: string) {
  if (!name) return 'Простір'
  const trimmedName = name.trim()
  return /^(Робочий\s+простір|Простір)\s*/i.test(trimmedName)
    ? trimmedName.replace(/^(Робочий\s+простір|Простір)\s*/i, 'Простір ')
    : `Простір ${trimmedName}`
}

function compactWorkspaceLabel(name?: string) {
  if (!name) return 'Простір'
  return name
    .trim()
    .replace(/^Робочий\s+простір\s*/i, '')
    .replace(/^Простір\s*/i, '')
    .trim() || 'Простір'
}

function entitlementLabel(entitlement?: WorkspaceEntitlement | null) {
  if (!entitlement) return ''
  if (entitlement.is_active) {
    return entitlement.status_label || 'Активний доступ'
  }
  return entitlement.status_label || 'Потрібно обрати тариф'
}

type WorkspaceSwitcherProps = {
  placement?: 'header' | 'sidebar'
  collapsed?: boolean
  compact?: boolean
  className?: string
}

export function WorkspaceSwitcher({ placement = 'header', collapsed = false, compact = false, className }: WorkspaceSwitcherProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const activeOrgId = useOrgStore((state) => state.activeOrgId)
  const contextQuery = useQuery<WorkspaceContext>({
    queryKey: ['organizations', 'context', activeOrgId],
    queryFn: async () => {
      const response = await apiClient.get('/api/v1/organizations/context/')
      return response.data as WorkspaceContext
    },
    enabled: Boolean(activeOrgId),
    staleTime: 30_000,
  })

  const organizations = contextQuery.data?.organizations || []
  const activeWorkspace =
    contextQuery.data?.active_organization ||
    organizations.find((organization) => organization.id === activeOrgId) ||
    null
  const canSwitch = organizations.length > 1
  const currentLabel = workspaceLabel(activeWorkspace?.name)
  const triggerLabel = compact ? compactWorkspaceLabel(activeWorkspace?.name) : currentLabel
  const statusLabel = entitlementLabel(activeWorkspace?.entitlement)
  const hideRedundantActiveStatus =
    !canSwitch && activeWorkspace?.role === 'OWNER' && activeWorkspace.entitlement?.is_active
  const triggerMeta = [activeWorkspace?.role_display, hideRedundantActiveStatus ? '' : statusLabel]
    .filter(Boolean)
    .join(' · ')
  const isSidebar = placement === 'sidebar'
  const isCollapsedSidebar = isSidebar && collapsed
  const showMeta = !compact

  function switchWorkspace(workspace: WorkspaceOption) {
    if (workspace.id === activeOrgId) return

    localStorage.setItem('lastActiveOrgId', workspace.id)
    navigate(buildWorkspaceSwitchPath(location.pathname, workspace.id))
  }

  if (!activeOrgId) {
    return null
  }

  if (contextQuery.isError) {
    return (
      <div className={cn(
        'items-center gap-2 rounded-[10px] border border-[var(--mph-warning)]/30 bg-[var(--mph-warning-soft)] px-2.5 py-1.5 text-xs text-[var(--mph-warning)]',
        isSidebar ? 'flex w-full' : 'hidden max-w-[220px] md:flex',
        className,
      )}>
        <Building2 className="h-3.5 w-3.5 shrink-0" />
        {!isCollapsedSidebar ? <span className="truncate">Простір недоступний</span> : null}
      </div>
    )
  }

  const trigger = (
    <button
      type="button"
      disabled={!canSwitch}
      className={cn(
        'flex items-center gap-2 rounded-[10px] border border-[var(--mph-border)] bg-[var(--mph-bg-control)] text-left text-xs text-[var(--mph-text-primary)] outline-none transition hover:bg-[var(--mph-bg-control-hover)] focus-visible:shadow-[var(--mph-focus-ring)]',
        isCollapsedSidebar
          ? 'h-9 w-9 justify-center px-0 py-0'
          : isSidebar && compact
            ? 'min-w-0 border-transparent bg-transparent px-1.5 py-1 hover:bg-[var(--mph-bg-control)]'
            : isSidebar
              ? 'w-full px-2.5 py-2'
            : 'max-w-[170px] px-2.5 py-1.5 sm:max-w-[240px] xl:max-w-[320px]',
        !canSwitch && 'cursor-default hover:bg-[var(--mph-bg-control)]',
        className,
      )}
      aria-label="Перемикач робочого простору"
      title={isCollapsedSidebar ? `${currentLabel}${triggerMeta ? ` · ${triggerMeta}` : ''}` : undefined}
    >
      {!compact ? <Building2 className="h-4 w-4 shrink-0 text-[var(--mph-accent)]" /> : null}
      {!isCollapsedSidebar ? (
        <span className="min-w-0 flex-1">
          <span className="block truncate font-semibold">{triggerLabel}</span>
          {showMeta ? (
            <span className="block truncate text-[11px] text-[var(--mph-text-tertiary)]">
              {triggerMeta || 'Завантаження...'}
            </span>
          ) : null}
        </span>
      ) : null}
      {canSwitch && !isCollapsedSidebar ? <ChevronDown className="h-3.5 w-3.5 shrink-0 text-[var(--mph-text-tertiary)]" /> : null}
    </button>
  )

  if (!canSwitch) {
    return trigger
  }

  return (
    <DropdownMenu.Root modal={false}>
      <DropdownMenu.Trigger asChild>{trigger}</DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align={isSidebar ? 'start' : 'end'}
          side={isCollapsedSidebar ? 'right' : 'bottom'}
          className="z-50 w-80 rounded-[14px] border border-[var(--mph-border)] bg-[var(--mph-bg-panel-raised)] p-1 text-sm text-[var(--mph-text-primary)] shadow-[var(--mph-shadow-panel)]"
        >
          <div className="px-3 py-2">
            <p className="text-xs font-semibold uppercase tracking-[0.08em] text-[var(--mph-text-tertiary)]">
              Робочі простори
            </p>
          </div>
          {organizations.map((workspace) => {
            const isActive = workspace.id === activeWorkspace?.id
            const itemStatusLabel = entitlementLabel(workspace.entitlement)
            return (
              <DropdownMenu.Item
                key={workspace.id}
                onClick={() => switchWorkspace(workspace)}
                className="flex cursor-pointer items-start gap-2 rounded-[10px] px-2.5 py-2 outline-none hover:bg-[var(--mph-bg-control)]"
              >
                <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-[9px] border border-[var(--mph-border-subtle)] bg-[var(--mph-bg-control)]">
                  <Building2 className="h-3.5 w-3.5 text-[var(--mph-accent)]" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-semibold">{workspaceLabel(workspace.name)}</span>
                  <span className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-[var(--mph-text-tertiary)]">
                    <span>{workspace.role_display}</span>
                    <MpStatusPill tone={workspace.entitlement.is_active ? 'success' : 'warning'} className="text-[10px]">
                      <ShieldCheck className="h-3 w-3" />
                      {itemStatusLabel}
                    </MpStatusPill>
                  </span>
                </span>
                <Check className={cn('mt-1 h-4 w-4 shrink-0 text-[var(--mph-accent)]', !isActive && 'opacity-0')} />
              </DropdownMenu.Item>
            )
          })}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  )
}
